import React, { useEffect, useState } from "react";
import { legacyHearingValue } from 'helpers/legacyHearingType';
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Api from "services/api";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { caseSelector, setError, updateCase, addCase, fetchCases } from "slices/caseSlice";
import { fetchCaseTypes } from "slices/caseTypeSlice";
import { hearingTypeSelector, fetchHearingTypes } from "slices/hearingTypeSlice";
import { fetchPartyTypes } from "slices/partyTypeSlice";
import { jobLocationSelector, fetchJobLocationCourtrooms, fetchJobLocationJurisdictions } from "slices/jobLocationSlice";
import ErrorMessage from "components/common/ErrorMessage";
import Swal from "sweetalert2";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles, Select } from "components/common/Select";
import { DialogTitle } from "@mui/material";
import { BootstrapDialog } from "components/subUsers/AddSubUsers";
import { DownloadIcon, Lock } from "lucide-react";
import moment from "moment";
import { usePermissions } from "layouts/Admin";

const AddCasesForm = ({ item, handleClose, callback, show, formattime, appointment }) => {
  const { handleSubmit, formState: { errors }, control, setValue, watch, clearErrors } = useForm();
  const dispatch = useDispatch();
  const { id: apptIdFromParams } = useParams();
  const apptId = appointment?.id || apptIdFromParams;
  // `total` = how many cases this job already has (CasesTable fetches with this appt_id)
  const { error, loader, total: existingCaseCount } = useSelector(caseSelector);

  const { hearingType } = useSelector(hearingTypeSelector);
  const { jobCourtrooms, jobJurisdictions } = useSelector(jobLocationSelector);

  const [filterPartyType, setFilterPartyType] = useState(null);
  const [filterCaseType, setFilterCaseType] = useState(null);
  const [filterHearingType, setFilterHearingType] = useState(null);
  const [filterCourtrooms, setFilterCourtrooms] = useState([]);
  const [filterJurisdictions, setFilterJurisdictions] = useState(null);

  const [selectedCourtroom, setSelectedCourtroom] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Case # lookup (King County DCOR API, proxied via backend)
  const [caseSearch, setCaseSearch] = useState("");          // text in the Case # input
  const [caseResults, setCaseResults] = useState([]);        // API matches
  const [caseLoading, setCaseLoading] = useState(false);
  const [caseLookupError, setCaseLookupError] = useState("");
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const [caseNameUnavailable, setCaseNameUnavailable] = useState(false);
  const caseDebounceRef = React.useRef(null);

  // Default hearing time = the job's scheduled start time (24h for the <input type="time">).
  // Fallback parses the formatted "MMM D, YYYY hh:mm A (PST)" string WITH its am/pm marker —
  // the old split(" ")[3] dropped the PM and defaulted evening jobs to AM.
  const timeOnly = appointment?.start_time
    ? moment(appointment.start_time, ["HH:mm:ss", "HH:mm"]).format("HH:mm")
    : moment((formattime || "").replace(" (PST)", ""), "MMM D, YYYY hh:mm A").format("HH:mm");
  const serviceType = watch("service_type");
  const defaultCourtAddress = appointment?.appointment_details?.location_address
    || appointment?.appointment_details?.job_location?.address || "";
  // Zoom job locations get a "Zoom" type instead of "In Person" (same behavior — only
  // "Document Translation" is special-cased anywhere)
  const isZoomLocation = !!appointment?.appointment_details?.job_location?.by_zoom;
  const inPersonOption = isZoomLocation ? "Zoom" : "In Person";
  // Admins can override the location-derived type — imported cases can disagree with their job's
  // zoom flag, and the client would rather correct those by hand than have us run a data script.
  // Everyone else keeps the derived value only (a <select> whose value isn't in its options
  // renders blank, so the option list and the prefill have to move together).
  const { isAdmin } = usePermissions();
  const serviceTypeOptions = isAdmin
    ? ["In Person", "Zoom", "Document Translation"]
    : [inPersonOption, "Document Translation"];
  console.log(timeOnly, "timeOnly")

  useEffect(() => {
    if (serviceType === "Document Translation") {
      setFilterPartyType(null);
      setValue("party_type_id", null);
      setFilterCaseType(null);
      setValue("case_type_id", null);
      setFilterHearingType(null);
      setValue("hearing_type_id", null);
      setFilterJurisdictions(null);
      setValue("jurisdiction_id", null);
    }
  }, [serviceType, setValue]);

  // Reset helpers
  const resetFields = () => {
    dispatch(setError({}));
    setValue("case_id", "");
    setValue("created_on", "");
    setValue("updated_on", "");
    setValue("updated_by", "");
    setValue("case_number", "");
    setValue("client_name", "");
    setCaseSearch("");
    setCaseResults([]);
    setShowCaseDropdown(false);
    setCaseLookupError("");
    setCaseNameUnavailable(false);
    setValue("case_type_id", "");
    setValue("hearing_type_id", "");
    setValue("notes", "");
    setValue("court_address", defaultCourtAddress);
    setValue("courtroom_id", "");
    setValue("jurisdiction_id", "");
    setFilterJurisdictions(null);
    setValue("party_type_id", "");
    setValue("service_type", inPersonOption);
    setValue("translation_document", null);
    setSelectedDocument(null);
  };

  // ----- Case # lookup (King County DCOR API) -----
  const applyCase = (c) => {
    if (!c) return;
    setValue("case_number", c.caseNumber, { shouldValidate: true });
    setValue("client_name", c.caseTitle || "");
    setCaseSearch(c.caseNumber);
    setCaseResults([]);
    setShowCaseDropdown(false);
    setCaseLookupError("");
  };

  const runCaseLookup = (value) => {
    const term = (value || "").trim();
    if (term.length < 3) {
      setCaseResults([]);
      setShowCaseDropdown(false);
      setCaseLoading(false);
      return;
    }
    setCaseLoading(true);
    Api.fetchKcCases(term)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
        if (res?.status >= 200 && res?.status < 300) {
          setCaseLookupError("");
          if (list.length === 1) {
            applyCase(list[0]);
          } else if (list.length > 1) {
            setCaseResults(list);
            setShowCaseDropdown(true);
          } else {
            setCaseResults([]);
            setShowCaseDropdown(false);
          }
        } else {
          setCaseResults([]);
          setShowCaseDropdown(false);
          setCaseLookupError(res?.data?.message || "Case lookup is currently unavailable.");
        }
      })
      .catch(() => {
        setCaseResults([]);
        setShowCaseDropdown(false);
        setCaseLookupError("Case lookup is currently unavailable.");
      })
      .finally(() => setCaseLoading(false));
  };

  const handleCaseSearchChange = (value) => {
    setCaseSearch(value);
    // any manual typing invalidates the current selection until a match is chosen
    setValue("case_number", "", { shouldValidate: false });
    setValue("client_name", "");
    if (caseDebounceRef.current) clearTimeout(caseDebounceRef.current);
    caseDebounceRef.current = setTimeout(() => runCaseLookup(value), 400);
  };

  const handleToggleCaseUnavailable = (checked) => {
    setCaseNameUnavailable(checked);
    if (caseDebounceRef.current) clearTimeout(caseDebounceRef.current);
    setCaseResults([]);
    setShowCaseDropdown(false);
    setCaseLookupError("");
    setCaseSearch("");
    setValue("case_number", "", { shouldValidate: false });
    setValue("client_name", "");
    // Clear validation errors for the fields that stop being required when the case
    // name isn't available
    if (checked) {
      clearErrors(["case_number", "party_type_id", "case_type_id", "hearing_type_id"]);
    }
  };

  console.log(item?.hearing_start_time, "item?.hearing_start_time");
  // Form initial values
  useEffect(() => {
    if (item?.id) {
      // agar update hai toh sirf item ke values set karo
      setValue("case_id", (item?.case_id || "").toUpperCase());
      setValue("created_on", item?.created_at ? new Date(item.created_at).toLocaleString() : "");
      setValue("updated_on", item?.updated_at ? new Date(item.updated_at).toLocaleString() : "");
      setValue("updated_by", item?.updated_by?.name || "");
      setValue("case_number", item?.case_number || "");
      setValue("client_name", item?.client_name || "");
      setCaseSearch(item?.case_number || "");
      setCaseNameUnavailable(!!item?.case_unavailable);
      setValue("case_type_id", item?.case_type_id || "");
      setValue("hearing_type_id", item?.hearing_type_id || "");
      setValue("notes", item?.notes || "");
      setValue("court_address", item?.court_address || "");
      setValue("courtroom_id", item?.courtroom_id || "");
      setValue("jurisdiction_id", item?.jurisdiction_id || "");
      setValue("party_type_id", item?.party_type_id || "");
      // Admins see the value actually stored on the case (so a mismatch with the job's zoom flag
      // is visible and correctable). For everyone else, legacy values are still coerced to the
      // location's type — Document Translation is always preserved as-is.
      setValue(
        "service_type",
        isAdmin && serviceTypeOptions.includes(item?.service_type)
          ? item.service_type
          : (item?.service_type === "Document Translation" ? "Document Translation" : inPersonOption)
      );
      setValue("translation_document", null);
      setSelectedDocument(null);
      setValue("hearing_start_time", item?.hearing_start_time || "");
    } else {
      setValue("hearing_start_time", item?.hearing_start_time || timeOnly);
      setValue("service_type", inPersonOption);
      setValue("court_address", defaultCourtAddress);
      setSelectedDocument(null);
      // agar naya record create karna ho toh resetFields
      resetFields();
    }
  }, [item, setValue, defaultCourtAddress, inPersonOption]);


  // #region Party type 

  useEffect(() => {
    if (item?.party_type?.id) {
      setFilterPartyType({
        label: item?.party_type?.name || "",
        value: item?.party_type?.id,
      });
    } else {
      setFilterPartyType(null);
    }
  }, [item?.party_type]);

  const filteredPartyTypeLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchPartyTypes(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((val) => ({ value: val.id, label: val.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  // #endregion


  // #region Case Type 

  useEffect(() => {
    if (item?.case_type?.id) {
      setFilterCaseType({
        label: item?.case_type?.name || "",
        value: item?.case_type?.id,
      });
    } else {
      setFilterCaseType(null);
    }
  }, [item?.case_type]);

  const filteredCaseTypeLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchCaseTypes(
        page,
        { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue },
        true,
        true
      )
    );
    return {
      options: result.data.map((val) => ({ value: val.id, label: val.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };
  //#endregion


  // #region HearingType options 
  const handleHearingReset = () => {
    setFilterHearingType(null);
    setValue("hearing_type_id", null);
  };

  useEffect(() => {
    if (item?.hearing_type?.id) {
      setFilterHearingType({
        label: item?.hearing_type?.name || "",
        value: item?.hearing_type?.id,
      });
    }
  }, [item?.hearing_type]);

  // Hearing types belong to a case type, so reload them whenever the SELECTED case
  // type changes (not just when editing a saved case). The API filters on `case_id`.
  const selectedCaseTypeId = watch("case_type_id");

  useEffect(() => {
    if (selectedCaseTypeId) {
      dispatch(
        fetchHearingTypes(1, {
          case_id: selectedCaseTypeId,
          pageLength: 1000,
          sortBy: "id",
          sortDirection: "asc",
        })
      );
    }
  }, [selectedCaseTypeId, dispatch]);

  // Changing case type invalidates any hearing type picked for the previous one
  const previousCaseTypeRef = React.useRef(null);
  useEffect(() => {
    if (previousCaseTypeRef.current !== null && previousCaseTypeRef.current !== selectedCaseTypeId) {
      handleHearingReset();
    }
    previousCaseTypeRef.current = selectedCaseTypeId;
  }, [selectedCaseTypeId]);

  //#endregion


  // #region Courtrooms
  useEffect(() => {
    if (show && apptId) {
      dispatch(fetchJobLocationCourtrooms(apptId));
      dispatch(fetchJobLocationJurisdictions(apptId));
    }
  }, [apptId, dispatch, show]);

  useEffect(() => {
    if (item?.courtroom?.id) {
      setFilterCourtrooms({
        label: item?.courtroom?.name || "",
        value: item?.courtroom?.id,
      });
    }
  }, [item?.courtroom]);

  useEffect(() => {
    if (item?.jurisdiction?.id) {
      setFilterJurisdictions({
        label: item?.jurisdiction?.name || "",
        value: item?.jurisdiction?.id,
      });
    }
  }, [item?.jurisdiction]);
  console.log(item, "item")


  //#endregion


  // Submit Handler
  const submitCallback = (status, message) => {
    if (status) {
      callback();
      toast.success(message, { className: "successToast" });
      dispatch(fetchCases(1, { appt_id: apptId }));
      handleClose();
      resetFields();
    } else {
      toast.error(message, { className: "errorToast" });
    }
  };

  const onSubmit = (data) => {
    dispatch(setError({}));
    const merged = {
      ...data,
      case_unavailable: caseNameUnavailable ? 1 : 0,
      case_number: caseNameUnavailable ? "" : data.case_number,
      client_name: data.client_name,
    };
    let finalData;
    if (merged.service_type === "Document Translation") {
      const fd = new FormData();
      const payload = { ...merged, appt_id: apptId };
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "translation_document") return;
        if (value !== undefined && value !== null) {
          fd.append(key, value);
        }
      });
      if (selectedDocument) {
        fd.append("translation_document", selectedDocument);
      }
      finalData = fd;
    } else {
      finalData = { ...merged, appt_id: apptId };
    }
    if (item?.id) {
      dispatch(updateCase(finalData, item?.id, submitCallback));
    } else if ((existingCaseCount ?? 0) >= 10) {
      // No hard limit any more (client, Aug 20 2026) — the old "Maximum limit of 10 cases
      // reached" 400 became this warning: the coordinator decides.
      Swal.fire({
        title: "That's a lot of cases",
        text: `This job already has ${existingCaseCount} cases. Add another anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, add the case",
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(addCase(finalData, submitCallback));
        }
      });
    } else {
      dispatch(addCase(finalData, submitCallback));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Read Only Fields */}
      <div className="read-only-fileds">
        {[
          { name: "case_id", label: "Algovi Case ID" },
          { name: "created_on", label: "Created on" },
          { name: "updated_on", label: "Updated on" },
          { name: "updated_by", label: "Updated by" },
        ].map(({ name, label }) => (
          <div className="row-primary" key={name}>
            <h2>{label}</h2>
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <input className="input-in type_inp" type="text" readOnly {...field} />
              )}
            />
          </div>
        ))}
      </div>

      {/* Case Number (King County DCOR lookup) */}
      <div className="row-primary">
        <h2>Case #{caseNameUnavailable ? '' : ' *'}</h2>
        <div className="w-100" style={{ position: "relative" }}>
          {/* Hidden RHF field that holds the validated/selected case number */}
          <Controller
            name="case_number"
            control={control}
            rules={{ required: caseNameUnavailable ? false : "Please select a valid case number from the suggestions" }}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <input
            className="input-in type_inp"
            type="text"
            placeholder="Type case number to search…"
            value={caseSearch}
            disabled={caseNameUnavailable}
            autoComplete="off"
            onChange={(e) => handleCaseSearchChange(e.target.value)}
            onFocus={() => { if (caseResults.length > 1) setShowCaseDropdown(true); }}
          />
          {caseLoading && (
            <div style={{ fontSize: 12, color: "#809FB8", marginTop: 4 }}>Searching…</div>
          )}
          {showCaseDropdown && caseResults.length > 0 && (
            <div
              style={{
                position: "absolute", zIndex: 20, left: 0, right: 0, top: "100%",
                background: "#fff", border: "1px solid #E1E7EC", borderRadius: 8,
                maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              {caseResults.map((c) => {
                const isClosed = !!c.caseClosedDate;
                return (
                  <div
                    key={c.caseID || c.caseNumber}
                    onClick={() => applyCase(c)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f4f7",
                      fontSize: 13,
                      color: isClosed ? "#bbb" : undefined,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    title={isClosed ? `Closed ${c.caseClosedDate}` : (c.isCaseSealed ? "Sealed case" : undefined)}
                  >
                    <strong style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {c.caseNumber?.toUpperCase()}
                      {c.isCaseSealed ? <Lock size={13} color="#b51a12" strokeWidth={2.5} /> : null}
                    </strong>
                    {c.caseTitle ? (
                      <span style={{ color: isClosed ? "#bbb" : "#809FB8" }}> — {c.caseTitle}</span>
                    ) : null}
                    {isClosed ? (
                      <span style={{ color: "#b51a12", marginLeft: 6, fontSize: 11, fontWeight: 600 }}>(Closed)</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          {caseLookupError && (
            <div style={{ marginTop: 6 }}>
              <ErrorMessage text={`${caseLookupError} If you can't find the case, check "Case name not available" below.`} />
            </div>
          )}
          {errors?.case_number?.message && (
            <div style={{ marginTop: 6 }}><ErrorMessage text={errors.case_number.message} /></div>
          )}
          {error?.errors?.case_number?.map((err, idx) => (
            <div style={{ marginBottom: 10 }} key={idx}>
              <ErrorMessage text={err} />
            </div>
          ))}

          {/* Case name not available */}
          <div className="row-primary align-items-center" style={{ marginTop: 10 }}>
            <label className="custom-checkbox checkbox">
              <input
                type="checkbox"
                id="case_name_not_available"
                checked={caseNameUnavailable}
                onChange={(e) => handleToggleCaseUnavailable(e.target.checked)}
              />
              <span className="checkmark"></span>
            </label>
            <label htmlFor="case_name_not_available" className="labelTitle m-0" style={{ cursor: "pointer" }}>
              Case name not available
            </label>
          </div>
        </div>
      </div>

      {/* Client Name — auto-filled from the selected case; typeable when the case is unavailable */}
      <div className="row-primary">
        <h2>Name{caseNameUnavailable ? '' : ' *'}</h2>
        <Controller
          name="client_name"
          control={control}
          render={({ field }) => (
            <input className="input-in type_inp" type="text" readOnly={!caseNameUnavailable} {...field} placeholder="Name" />
          )}
        />
      </div>

      {/* Service Type */}
      <div className="row-primary">
        <h2>Type *</h2>
        <Controller
          name="service_type"
          control={control}
          rules={{ required: "Please select a service type" }}
          render={({ field }) => (
            <select className="input-in type_inp" {...field}>
              {serviceTypeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          )}
        />
        {errors?.service_type?.message && (
          <ErrorMessage text={errors.service_type.message} />
        )}
      </div>

      {watch("service_type") === "Document Translation" && (
        <div className="row-primary">
          <h2>Upload document</h2>
          <div className="w-100">
            <input
              type="file"
              className="input-in type_inp"
              style={{ paddingTop: '10px' }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                // Preflight the 50MB cap so oversized scans fail here with a clear message,
                // not server-side after the whole upload
                if (file && file.size > 50 * 1024 * 1024) {
                  toast.error(`This document is ${(file.size / (1024 * 1024)).toFixed(1)}MB — the limit is 50MB. Please compress the file and try again.`, { className: "errorToast" });
                  e.target.value = "";
                  setSelectedDocument(null);
                  setValue("translation_document", null);
                  return;
                }
                setSelectedDocument(file);
                setValue("translation_document", file);
              }}
            />
            {item?.translation_document && (
              <button
                type="button"
                className="main_button mt-2 d-flex align-items-center gap-2"
                onClick={async () => {
                  try {
                    const response = await Api.downloadTranslatefile(item.translation_document);
                    if (!response) return;
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = item.translation_document.split("/").pop();
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (error) {
                    console.log("Download error", error);
                  }
                }}
              >
                <DownloadIcon size={16} />
                Download uploaded file
              </button>
            )}
          </div>
        </div>
      )}


      {/* Party Type */}
      {/* <div className="row-primary">
        <h2>Party Type *</h2>
        <Controller
          name="party_type_id"
          control={control}
          rules={{ required: "Please select a party type" }}
          render={({ field }) => (
            <AsyncPaginate
              menuPlacement="auto"
              styles={{ ...selectCustomStyles }}
              value={filterPartyType}
              loadOptions={filteredPartyTypeLoadOptions}
              isClearable
              escapeClearsValue
              onChange={(obj) => {
                if (obj?.value) {
                  setFilterPartyType(obj);
                  field.onChange(obj.value);
                } else {
                  setFilterPartyType(null);
                  field.onChange(null);
                }
              }}
              additional={{ page: item?.state?.id ? 2 : 1 }}
            />
          )}
        />
        {errors?.party_type_id?.message && (
          <ErrorMessage text={errors.party_type_id.message} />
        )}
      </div> */}
      {watch("service_type") !== "Document Translation" && (
        <div className="row-primary">
          <h2>Party Type{caseNameUnavailable ? '' : ' *'}</h2>
          <Controller
            name="party_type_id"
            control={control}
            rules={{ required: caseNameUnavailable ? false : "Please select a party type" }}
            render={({ field }) => (
              <AsyncPaginate
                menuPlacement="auto"
                styles={{ ...selectCustomStyles }}
                value={filterPartyType}
                loadOptions={filteredPartyTypeLoadOptions}
                isClearable
                escapeClearsValue
                onChange={(obj) => {
                  if (obj?.value) {
                    setFilterPartyType(obj);
                    field.onChange(obj.value);
                  } else {
                    setFilterPartyType(null);
                    field.onChange(null);
                  }
                }}
                additional={{ page: item?.state?.id ? 2 : 1 }}
              />
            )}
          />
          {errors?.party_type_id?.message && (
            <ErrorMessage text={errors.party_type_id.message} />
          )}
        </div>
      )}

      {/* Case Type */}
      {serviceType !== "Document Translation" && (
        <div className="row-primary">
          <h2>Case Type{caseNameUnavailable ? '' : ' *'}</h2>
          <Controller
            name="case_type_id"
            control={control}
            rules={{ required: caseNameUnavailable ? false : "Please select a case type" }}
            render={({ field }) => (
              <AsyncPaginate
                menuPlacement="auto"
                styles={{ ...selectCustomStyles }}
                value={filterCaseType}
                loadOptions={filteredCaseTypeLoadOptions}
                isClearable
                escapeClearsValue
                onChange={(obj) => {
                  if (obj?.value) {
                    setFilterCaseType(obj);
                    field.onChange(obj.value);
                    dispatch(
                      fetchHearingTypes(1, {
                        case_type_id: obj.value,
                        pageLength: 1000,
                        sortBy: "id",
                        sortDirection: "asc",
                      })
                    );
                  } else {
                    setFilterCaseType(null);
                    field.onChange(null);
                  }
                  handleHearingReset();
                }}
                additional={{ page: 1 }}
              />
            )}
          />
          {errors?.case_type_id?.message && (
            <ErrorMessage text={errors.case_type_id.message} />
          )}
        </div>
      )}

      {/* Hearing Type */}
      {serviceType !== "Document Translation" && (
        <div className="row-primary">
          <h2>Hearing Type{caseNameUnavailable ? '' : ' *'}</h2>
          <Controller
            name="hearing_type_id"
            control={control}
            rules={{ required: caseNameUnavailable ? false : "Please select a hearing type" }}
            render={({ field }) => (
              <Select
                menuPlacement="auto"
                styles={{ ...selectCustomStyles }}
                value={filterHearingType}
                options={
                  watch("case_type_id")
                    ? hearingType.map((i) => ({ label: i.name, value: i.id }))
                    : []
                }
                onChange={(val) => {
                  field.onChange(val);
                }}
                onStateChanges={(obj) => {
                  setFilterHearingType(obj);
                }}
              />
            )}
          />
          {errors?.hearing_type_id?.message && (
            <ErrorMessage text={errors.hearing_type_id.message} />
          )}
          {/* editing an IMPORTED case: show what the old system called it, so whoever picks a real
              hearing type can see the original single value (it was imported as the Case Type) */}
          {legacyHearingValue(item) && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#809fb8', fontStyle: 'italic' }}>
              Old system value: {legacyHearingValue(item)}
            </p>
          )}
        </div>
      )}
      {/* Courtroom */}
      {serviceType !== "Document Translation" && (
      <div className="row-primary">
        <h2>Courtroom</h2>
        <Controller
          name="courtroom_id"
          control={control}
          render={({ field }) => (
            <Select
              menuPlacement="auto"
              styles={{ ...selectCustomStyles }}
              value={filterCourtrooms}
              options={jobCourtrooms?.map((i) => ({ label: i?.courtroom?.name, value: i?.courtroom?.id })) || []}
              onChange={(val) => {
                field.onChange(val);
              }}
              onStateChanges={(obj) => {
                setFilterCourtrooms(obj);
              }}
            />
          )}
        />
        {errors?.courtroom_id?.message && (
          <ErrorMessage text={errors.courtroom_id.message} />
        )}
      </div>
      )}

      {/* Jurisdiction */}
      {serviceType !== "Document Translation" && (
      <div className="row-primary">
        <h2>Jurisdiction</h2>
        <Controller
          name="jurisdiction_id"
          control={control}
          render={({ field }) => (
            <Select
              menuPlacement="auto"
              styles={{ ...selectCustomStyles }}
              value={filterJurisdictions}
              options={jobJurisdictions?.map((i) => ({ label: i?.jurisdiction?.name, value: i?.jurisdiction?.id })) || []}
              onChange={(val) => {
                field.onChange(val);
              }}
              onStateChanges={(obj) => {
                setFilterJurisdictions(obj);
              }}
            />
          )}
        />
        {errors?.jurisdiction_id?.message && (
          <ErrorMessage text={errors.jurisdiction_id.message} />
        )}
      </div>
      )}

      {/* Court Address */}
      {serviceType !== "Document Translation" && (
      <div className="row-primary">
        <h2>Court Address</h2>
        <Controller
          name="court_address"
          control={control}
          render={({ field }) => (
            <textarea className="type_inp input-in" {...field} placeholder="Court Address" />
          )}
        />
      </div>
      )}

      {/* Hearing Start Time */}
      {serviceType !== "Document Translation" && (
      <div className="row-primary">
        <h2>Hearing Start Time *</h2>
        <Controller
          name="hearing_start_time"
          control={control}
          rules={{ required: "Please select hearing start time" }}
          render={({ field }) => (
            <div className="input-image" style={{ padding: "0", border: "none" }}>
              <input
                type="time"
                className="input-in type_inp"
                {...field}
              />
              <img className="custom-icon" src="/images/clock.png" alt="" />
            </div>
          )}
        />
        {errors?.hearing_start_time?.message && (
          <ErrorMessage text={errors.hearing_start_time.message} />
        )}
      </div>
      )}

      {/* Notes */}
      <div className="row-primary">
        <h2>Case Notes</h2>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <textarea className="type_inp input-in" {...field} placeholder="Case notes" />
          )}
        />
      </div>

      {/* Global Errors */}
      {error?.errors?.name?.map((err, idx) => (
        <ErrorMessage key={idx} text={err} />
      ))}

      {/* Footer */}
      <div className="Form-footer">
        <button type="button" className="Cancle_button" onClick={handleClose}>
          Cancel
        </button>
        <SubmitButton loader={loader} btnText={item?.id ? "Update" : "Create"} />
      </div>
    </form>
  );
};

const AddCases = ({ item, onClose, show, callback, statusPerm, appointment, formattedtime }) => (
  <BootstrapDialog
    fullWidth={true}
    maxWidth={"md"}
    onClose={onClose}
    aria-labelledby="customized-dialog-title"
    open={show}
  >
    <div className="">
      <div className="modal-content full_modal">
        <DialogTitle>
          <div className="modal-header header_modal align-items-center">
            <div></div>
            <h5 className="modal-title_u">{item?.id ? "Update" : "Add"} Cases</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => onClose(item?.id, item?.status)} ></button>
          </div>
        </DialogTitle>
        <div className="modal-body modal_form_body">
          <AddCasesForm
            formattime={formattedtime}
            item={item}
            handleClose={onClose}
            show={show}
            callback={callback}
            statusPerm={statusPerm}
            appointment={appointment}
          />
        </div>
      </div>
    </div>
  </BootstrapDialog>
);

export default AddCases;